import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import 'lazysizes';
import 'lazysizes/plugins/unveilhooks/ls.unveilhooks';

import { placeholder43Url, prepareImgixParams } from '../../services/APIClient';
import { ANALYTICS_NAME } from './Constants';
import { UserResource } from '../../services/RESTClient';
import Tooltip from '../Tooltip';

const LightLink = styled.a.attrs({
  className: 'gray-600',
})`
  &{
    &:hover {
      color: black !important;
      text-decoration: underline;
    }
  }
`;

const userResource = new UserResource();

const FirmLink = ({
  index, firmName, firmNames, handleClick,
}) => (
  <Tooltip placement="bottom" tooltip="View all firm ideas">
    <LightLink
      href={null}
      onClick={() => handleClick(firmName)}
    >
      {firmName}
    </LightLink>
    {index + 1 < firmNames.length && <>, </>}
  </Tooltip>
);

FirmLink.propTypes = {
  index: PropTypes.number,
  firmName: PropTypes.string.isRequired,
  firmNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleClick: PropTypes.func,
};

FirmLink.defaultProps = {
  index: 0,
  handleClick: () => {},
};

const openLink = (event, link, target = '_blank') => {
  event.stopPropagation();
  window.open(link, target);
};

const ImageCard = ({
  showSettings,
  image,
  sendAnalytics,
  onImageClick,
  openRequireRegisterPopup,
  canSaveImage,
  onSaveImageClick,
  filterByFirmName,
}) => {
  const canCollect = userResource.canCollect();
  return (
    <div className="column column-block">
      <div className="card responsive-image">
        <div
          className="img-standard image-overlay-info"
          style={{
            backgroundImage: `url(${placeholder43Url})`,
            cursor: 'zoom-in',
          }}
          onClick={() => onImageClick(image)}
          role="button"
          tabIndex={0}
        >
          <div
            data-bg={
              image.url ? prepareImgixParams(image.url, { w: 450, h: 350, auto: 'format,compress' }) : placeholder43Url
            }
            className="lazyload p-a img-standard"
            style={{ top: 0 }}
          />
          <div className="overlay" />
          <div className="info p-0">
            <div className="row align-middle collapse full-height">
              <div className="column">
                <div className="control top p-s">
                  <div className="grid-x align-center full-height">
                    <div className="cell shrink">
                      <Tooltip
                        placement="top"
                        tooltip="Add To Collection"
                      >
                        <a
                          className="button tiny br-max black-button black-button-transparent pl-xs pr-xs"
                          href={null}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (canCollect) {
                              window.openAddToCollectionPopup({
                                mediaAttrId: image.mediaAttributionId,
                              });
                            } else {
                              openRequireRegisterPopup();
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <i className="material-icons">playlist_add</i>
                        </a>
                      </Tooltip>
                    </div>
                    <div className="cell shrink ml-xs">
                      <Tooltip
                        placement="top"
                        tooltip="View Original"
                      >
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href={canSaveImage ? `/image-download/${image.mediaAttributionId}` : null}
                          className="button tiny br-max black-button black-button-transparent pl-xs pr-xs"
                          onClick={(event) => {
                            event.stopPropagation();
                            onSaveImageClick(image);
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <i className="material-icons">fullscreen</i>
                        </a>
                      </Tooltip>
                    </div>
                    {showSettings && (
                    <div className="cell shrink ml-xs">
                      <Tooltip
                        placement="top"
                        tooltip="Edit Media Item"
                      >
                        <a
                          className="button tiny br-max black-button black-button-transparent pl-xs pr-xs"
                          onClick={e => openLink(e, `/admin/media/mediaitem/${image.mediaId}/change/`)}
                          role="button"
                          tabIndex={0}
                        >
                          <i className="material-icons">settings</i>
                        </a>
                      </Tooltip>
                    </div>
                    )}
                  </div>
                </div>
                <div className="control bottom p-s">
                  <div className="grid-x" style={{ height: '100%' }}>
                    {image.projectUrl && (
                    <div className="cell shrink">
                      <a
                        className="button tiny br-max black-button black-button-transparent pl-xs pr-xs"
                        onClick={(e) => {
                          sendAnalytics(ANALYTICS_NAME.RESULT_CLICKED, {
                            source: 'project_page',
                            url: image.projectUrl,
                          });
                          openLink(e, image.projectUrl);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <i className="material-icons mr-xxs">call_made</i>
                        <span>View Project</span>
                      </a>
                    </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="grid-x pt-xxs">
          <div className="cell auto">
            <p className="m-0 ellipsis gray-800 fs-s">{image.projectName}</p>
            {image.firmNames && image.firmNames.length
              ? (
                <div className="m-0 ellipsis gray-600 fs-s">
                  By
                  {' '}
                  {image.firmNames.map((firmName, i) => (
                    <FirmLink
                      key={firmName}
                      index={i}
                      firmName={firmName}
                      firmNames={image.firmNames}
                      handleClick={filterByFirmName}
                    />
                  ))}
                  .
                </div>
              )
              : <></>}
          </div>
        </div>
      </div>
    </div>
  );
};

ImageCard.propTypes = {
  showSettings: PropTypes.bool,
  canSaveImage: PropTypes.bool,
  onSaveImageClick: PropTypes.func,
  image: PropTypes.shape({
    globalId: PropTypes.string,
    mediaId: PropTypes.string,
    projectName: PropTypes.string,
    firmNames: PropTypes.arrayOf(PropTypes.string),
    firmSlugs: PropTypes.arrayOf(PropTypes.string),
    url: PropTypes.string,
    projectUrl: PropTypes.string,
    mediaAttributionId: PropTypes.string,
  }),
  sendAnalytics: PropTypes.func,
  onImageClick: PropTypes.func,
  openRequireRegisterPopup: PropTypes.func,
  filterByFirmName: PropTypes.func,
};

ImageCard.defaultProps = {
  showSettings: false,
  image: {
    globalId: '',
    mediaId: '',
    projectName: '',
    firmNames: [],
    firmSlugs: [],
    url: '',
    projectUrl: '',
    mediaAttributionId: '',
  },
  openRequireRegisterPopup: () => {},
  sendAnalytics: () => {},
  onImageClick: () => {},
  canSaveImage: false,
  onSaveImageClick: () => {},
  filterByFirmName: () => {},
};

export default ImageCard;
